import { ApiClient } from './api';
import { Application } from './components/application';
import { BrowserObjectModel } from './components/browser-object-model';
import { ApplicationContext } from './components/context/application.context';
import { UserContext } from './components/context/user.context';
import { ContainerBuilder } from './components/dependency-injection/container-builder';
import { Kernel as BaseKernel } from './components/dependency-injection/kernel';
import { Reference } from './components/dependency-injection/reference';
import { EventManager } from './components/event-manager/event-manager';
import { HttpClient } from './components/http-client/http-client';
import { IntegrationRunner } from './components/integrations/integration-runner';
import { SwoopCallbackOnlyIntegration } from './components/integrations/swoop-callback-only.integration';
import { SwoopDefaultIntegration } from './components/integrations/swoop-default.integration';
import { SwoopPassthroughIntegration } from './components/integrations/swoop-passthrough.integration';
import { UserMetricIntegration } from './components/integrations/user-metric.integration';
import { Logger } from './components/logger/logger';
import { NpiDetector } from './components/npi/npi.detector';
import { DmdNpiSource } from './components/npi/source/dmd-npi.source';
import { LhkNpiSource } from './components/npi/source/lhk-npi.source';
import { DoiMetadataProcessor } from './components/processor/metadata/doi-metadata.processor';
import { TitleMetadataProcessor } from './components/processor/metadata/title-metadata.processor';
import { DoiProvider } from './components/provider/doi.provider';
import { StylesheetProvider } from './components/provider/stylesheet.provider';
import { TitleProvider } from './components/provider/title.provider';
import { AdSetRenderer } from './components/renderer/ad-set.renderer';
import { AdSlotContainerRenderer } from './components/renderer/ad-slot-container.renderer';
import { AdSlotRenderer } from './components/renderer/ad-slot.renderer';
import { StylesheetRenderer } from './components/renderer/stylesheet.renderer';
import { AdSetResolver } from './components/resolver/ad-set.resolver';
import { AdSlotContainerItemResolver } from './components/resolver/ad-slot-container-item.resolver';
import { AdSlotContainerResolver } from './components/resolver/ad-slot-container.resolver';
import { AdSlotResolver } from './components/resolver/ad-slot.resolver';
import { MetadataResolver } from './components/resolver/metadata.resolver';
import { AdSlotRenderValidator } from './components/validator/ad-slot-render.validator';
import { AdSlotConstraintNpiValidator } from './components/validator/constraint/ad-slot-constraint-npi.validator';
import { AdSlotContainerItemView } from './containers/ad-slot-container-item.view';
import { AdSlotContainerView } from './containers/ad-slot-container.view';
import { ImageCreativeView } from './containers/image-creative.view';
import { LinkCreativeView } from './containers/link-creative.view';
import { AdSetSubscriber } from './event-subscribers/ad-set.subscriber';
import { AdSlotContainerSubscriber } from './event-subscribers/ad-slot-container.subscriber';
import { AdSlotSubscriber } from './event-subscribers/ad-slot.subscriber';
import { ApplicationSubscriber } from './event-subscribers/application.subscriber';
import { BackendSubscriber } from './event-subscribers/backend.subscriber';
import { IntegrationsSubscriber } from './event-subscribers/integrations.subscriber';
import { NpiDetectionSubscriber } from './event-subscribers/npi-detection.subscriber';
import { TelemetrySubscriber } from './event-subscribers/telemetry.subscriber';

export class Kernel extends BaseKernel {
  public constructor (private readonly applicationContext: ApplicationContext) {
    super(applicationContext.isDebug());
  }

  protected override getKernelParameters (): Record<string, unknown> {
    return {
      ...super.getKernelParameters(),
      'kernel.version': this.applicationContext.getVersion(),
      'kernel.sessionId': this.applicationContext.getSessionId(),
    };
  }

  protected override build (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(ApplicationContext, this.applicationContext);

    containerBuilder
      .register(Logger, Logger)
      .addMethodCall(
        'setEnabled',
        [ containerBuilder.hasParameter('kernel.debug') && containerBuilder.getParameter('kernel.debug') ],
      );

    containerBuilder.set(UserContext, UserContext);
    containerBuilder.set(Application, Application);
    containerBuilder.set(BrowserObjectModel, BrowserObjectModel);

    containerBuilder.set(StylesheetProvider, StylesheetProvider);
    containerBuilder.set(StylesheetRenderer, StylesheetRenderer);

    this.registerMetadataConfiguration(containerBuilder);
    this.registerIntegrationConfiguration(containerBuilder);
    this.registerAdSetConfiguration(containerBuilder);
    this.registerAdSlotConfiguration(containerBuilder);
    this.registerNpiDetectorConfiguration(containerBuilder);
    this.registerApiClientConfiguration(containerBuilder);
    this.registerEventManagerConfiguration(containerBuilder);
  }

  private registerMetadataConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(TitleProvider, TitleProvider);
    containerBuilder.set(DoiProvider, DoiProvider);

    containerBuilder.register(TitleMetadataProcessor, TitleMetadataProcessor).addTag('MetadataProcessor');
    containerBuilder.register(DoiMetadataProcessor, DoiMetadataProcessor).addTag('MetadataProcessor');
    containerBuilder.set(MetadataResolver, MetadataResolver);
  }

  private registerIntegrationConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.register(UserMetricIntegration, UserMetricIntegration).addTag('Integration');
    containerBuilder.register(SwoopDefaultIntegration, SwoopDefaultIntegration).addTag('Integration');
    containerBuilder.register(SwoopPassthroughIntegration, SwoopPassthroughIntegration).addTag('Integration');
    containerBuilder.register(SwoopCallbackOnlyIntegration, SwoopCallbackOnlyIntegration).addTag('Integration');
    containerBuilder.set(IntegrationRunner, IntegrationRunner);
  }

  private registerAdSetConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(AdSetResolver, AdSetResolver);
    containerBuilder.set(AdSetRenderer, AdSetRenderer);
  }

  private registerAdSlotConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(AdSlotResolver, AdSlotResolver);
    containerBuilder.set(AdSlotContainerResolver, AdSlotContainerResolver);
    containerBuilder.set(AdSlotContainerItemResolver, AdSlotContainerItemResolver);
    containerBuilder.set(AdSlotRenderer, AdSlotRenderer);
    containerBuilder.set(AdSlotContainerRenderer, AdSlotContainerRenderer);
    containerBuilder.set(AdSlotRenderValidator, AdSlotRenderValidator);
    containerBuilder.register(AdSlotConstraintNpiValidator, AdSlotConstraintNpiValidator).addTag('AdSlotConstraintValidator');

    // views
    containerBuilder.set(AdSlotContainerView, AdSlotContainerView);
    containerBuilder.set(AdSlotContainerItemView, AdSlotContainerItemView);
    containerBuilder.register(LinkCreativeView, LinkCreativeView).addTag('AdSlotContainerItemViewCreative');
    containerBuilder.register(ImageCreativeView, ImageCreativeView).addTag('AdSlotContainerItemViewCreative');
  }

  private registerNpiDetectorConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(NpiDetector, NpiDetector);
    containerBuilder.register(LhkNpiSource, LhkNpiSource).addTag('NpiSource');
    containerBuilder.register(DmdNpiSource, DmdNpiSource).addTag('NpiSource');
  }

  private registerApiClientConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.register(HttpClient, HttpClient)
      .addMethodCall('withOptions', [
        {
          baseUri: this.applicationContext.getApplicationConfig().apiUrl,
        },
      ]);

    const headers = {
      'X-Revops-Version': this.applicationContext.getVersion(),
      'X-Revops-Session-Id': this.applicationContext.getSessionId(),
      'X-Revops-Source-Type': 2, // SourceTypeEnum.WIDGET
      'X-Revops-Source-Url': this.applicationContext.getWindow().actualUrl ?? this.applicationContext.getWindow().location.href,
    };

    if (this.applicationContext.isDebug() && this.applicationContext.getApplicationConfig().userProperties?.qaIp) {
      headers[ 'X-Revops-QA-Ip' ] = this.applicationContext.getApplicationConfig().userProperties?.qaIp;
    }

    containerBuilder.register('backendHttpClient', HttpClient)
      .addMethodCall('withOptions', [
        {
          baseUri: this.applicationContext.getApplicationConfig().apiUrl,
          headers,
        },
      ])
    ;

    containerBuilder.set(ApiClient, ApiClient);
  }

  private registerEventManagerConfiguration (containerBuilder: ContainerBuilder): void {
    containerBuilder.set(AdSetSubscriber, AdSetSubscriber);
    containerBuilder.set(AdSlotSubscriber, AdSlotSubscriber);
    containerBuilder.set(AdSlotContainerSubscriber, AdSlotContainerSubscriber);
    containerBuilder.set(ApplicationSubscriber, ApplicationSubscriber);
    containerBuilder.set(BackendSubscriber, BackendSubscriber);
    containerBuilder.set(NpiDetectionSubscriber, NpiDetectionSubscriber);
    containerBuilder.set(IntegrationsSubscriber, IntegrationsSubscriber);
    containerBuilder.set(TelemetrySubscriber, TelemetrySubscriber);

    containerBuilder.register(EventManager, EventManager)
      .addMethodCall('addSubscriber', [ new Reference(AdSetSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(AdSlotSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(AdSlotContainerSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(ApplicationSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(BackendSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(NpiDetectionSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(IntegrationsSubscriber) ])
      .addMethodCall('addSubscriber', [ new Reference(TelemetrySubscriber) ])
    ;
  }
}
